import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Select from 'react-select';
import CircularProgress from '@mui/material/CircularProgress';
import PeopleIcon from '@mui/icons-material/People';

const C = {
  accent: '#2dd4bf', bg: '#f0f4f4', card: '#ffffff',
  cardBorder: 'rgba(0,0,0,0.06)', text: '#1a2e2e', muted: '#5a7a7a',
  shadow: '0 2px 10px rgba(0,0,0,0.07)', danger: '#ef4444',
};
const inp = { border: `1px solid ${C.cardBorder}`, borderRadius: '7px', fontSize: '12px', height: '28px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#f0f4f4', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'default', color: C.text };
const card = { backgroundColor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, marginBottom: '10px', overflow: 'hidden' };
const cardHead = (color = C.accent) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${C.cardBorder}`, backgroundColor: `${color}18`, fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.5px' });
const cardBody = { padding: '10px 12px' };
const lbl = { fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };

const selectStyles = {
  control: (base) => ({ ...base, minHeight: '28px', height: '28px', fontSize: '12px', borderRadius: '7px', borderColor: C.cardBorder }),
  valueContainer: (base) => ({ ...base, height: '28px', padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, height: '28px' }),
  menu: (base) => ({ ...base, fontSize: '12px' }),
};

const CLAIM_CATEGORIES = [
  { value: '1', label: 'Master' },
  { value: '2', label: 'Transaction' },
  { value: '3', label: 'Accounts' },
  { value: '4', label: 'Reports' },
  { value: '5', label: 'Administration' },
];

const UserDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, userId, logout, permissions, token } = useAuth();

  const hasEdit = permissions.includes('Edit Administration');

  const [isSavingRoles, setIsSavingRoles]   = useState(false);
  const [isSavingClaims, setIsSavingClaims] = useState(false);
  const [isEditMode, setIsEditMode]         = useState(false);
  const [isClaimEditMode, setIsClaimEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(CLAIM_CATEGORIES[0]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage]     = useState('');

  const [formData, setFormData] = useState({ FirstName: '', LastName: '', UserName: '', Email: '', ContactNumber: '', Roles: [] });
  const [formDataClaim, setFormDataClaim]   = useState({ Claim: [] });

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchData('UserApi', `/UserEdit/${id}`, token)
      .then(res => {
        if (res.responseCode === '1111') {
          const { User } = res.response_data;
          setFormData(prev => ({ ...prev, ...User, Roles: (res.response_data.Roles || []).map(r => ({ ...r })) }));
          setFormDataClaim(prev => ({ ...prev, ...User, Claim: (res.response_data.Claims || []).map(c => ({ ...c })) }));
        }
      })
      .catch(err => console.error('Error fetching user details:', err));
  }, [id]);

  const getClaimRange = () => {
    const start = (parseInt(selectedCategory.value, 10) - 1) * 5;
    return { start, end: start + 4 };
  };

  const handleChangeRole = (index) => {
    setFormData(prev => ({
      ...prev,
      Roles: prev.Roles.map((r, i) => ({ ...r, IsSelected: i === index ? !r.IsSelected : false })),
    }));
  };

  const handleChangeClaim = (index) => {
    setFormDataClaim(prev => {
      const updated = [...prev.Claim];
      updated[index] = { ...updated[index], IsSelected: !updated[index].IsSelected };
      return { ...prev, Claim: updated };
    });
  };

  const saveRoles = async () => {
    setIsSavingRoles(true);
    try {
      await updateData('UserApi', `SaveUserRoles/tmp/${id}`, formData, token);
      setSuccessMessage('Roles saved successfully');
      setIsEditMode(false);
      history.push(`/userDetail/${id}`);
    } catch (err) { console.error('Error saving roles:', err); }
    finally { setIsSavingRoles(false); }
  };

  const saveClaims = async () => {
    setIsSavingClaims(true);
    try {
      await updateData('UserApi', `SaveUserClaims/UpdateClaim/tmp/${id}`, formDataClaim, token);
      setSuccessMessage('Claims saved successfully');
      setIsClaimEditMode(false);
      history.push(`/userDetail/${id}`);
    } catch (err) { console.error('Error saving claims:', err); }
    finally { setIsSavingClaims(false); }
  };

  const { start, end } = getClaimRange();
  const visibleClaims = formDataClaim.Claim.filter((_, i) => i >= start && i <= end);

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: C.bg }}>

      {/* ── Header ── */}
      <div style={{ ...card, overflow: 'visible' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <PeopleIcon style={{ fontSize: 16, color: C.accent }} />
            <span style={{ fontWeight: 800, fontSize: '13px', color: C.text, fontFamily: 'Inter, sans-serif' }}>User Details</span>
          </div>
          <Link to="/UserIndex"
            style={{ height: '28px', padding: '0 14px', borderRadius: '7px', border: `1px solid ${C.cardBorder}`, backgroundColor: C.card, color: C.muted, fontWeight: 600, fontSize: '11px', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            ← Back
          </Link>
        </div>
      </div>

      {errorMessage   && <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px', fontSize: '11px', color: C.danger }}>{errorMessage}</div>}
      {successMessage && <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px', fontSize: '11px', color: '#16a34a' }}>{successMessage}</div>}

      {/* ── User Info ── */}
      <div style={card}>
        <div style={cardHead()}>👤 User Info</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
          <div><label style={lbl}>First Name</label><input type="text" value={formData.FirstName || ''} readOnly style={inp} /></div>
          <div><label style={lbl}>Last Name</label><input type="text" value={formData.LastName || ''} readOnly style={inp} /></div>
          <div><label style={lbl}>User Name</label><input type="text" value={formData.UserName || ''} readOnly style={inp} /></div>
          <div><label style={lbl}>Contact</label><input type="text" value={formData.ContactNumber || ''} readOnly style={inp} /></div>
          <div><label style={lbl}>Email</label><input type="text" value={formData.Email || ''} readOnly style={inp} /></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>

        {/* ── User Claims ── */}
        <div style={card}>
          <div style={{ ...cardHead('#8b5cf6'), justifyContent: 'space-between' }}>
            <span>🔑 User Claims</span>
            {hasEdit && (
              <button onClick={isClaimEditMode ? saveClaims : () => setIsClaimEditMode(true)} disabled={isSavingClaims}
                style={{ height: '22px', padding: '0 10px', borderRadius: '5px', border: 'none', backgroundColor: isClaimEditMode ? C.accent : '#8b5cf6', color: '#fff', fontWeight: 700, fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
                {isSavingClaims ? <CircularProgress size={10} sx={{ color: '#fff' }} /> : isClaimEditMode ? '✓ Save Claims' : 'Manage Claims'}
              </button>
            )}
          </div>
          <div style={cardBody}>
            <div style={{ marginBottom: '8px' }}>
              <Select options={CLAIM_CATEGORIES} value={selectedCategory} onChange={setSelectedCategory} styles={selectStyles} />
            </div>
            {visibleClaims.length > 0 ? visibleClaims.map((claim, i) => {
              const realIndex = start + i;
              return (
                <div key={realIndex} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${C.cardBorder}` }}>
                  <span style={{ fontSize: '12px', color: C.text }}>{claim.ClaimValue}</span>
                  <input type="checkbox" checked={claim.IsSelected} onChange={() => handleChangeClaim(realIndex)} disabled={!isClaimEditMode}
                    style={{ width: 14, height: 14, cursor: isClaimEditMode ? 'pointer' : 'default', accentColor: C.accent }} />
                </div>
              );
            }) : <p style={{ fontSize: '12px', color: C.muted }}>No claims available.</p>}
          </div>
        </div>

        {/* ── User Roles ── */}
        <div style={card}>
          <div style={{ ...cardHead('#f59e0b'), justifyContent: 'space-between' }}>
            <span>🎭 User Roles</span>
            {hasEdit && (
              <button onClick={isEditMode ? saveRoles : () => setIsEditMode(true)} disabled={isSavingRoles}
                style={{ height: '22px', padding: '0 10px', borderRadius: '5px', border: 'none', backgroundColor: isEditMode ? C.accent : '#f59e0b', color: '#fff', fontWeight: 700, fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
                {isSavingRoles ? <CircularProgress size={10} sx={{ color: '#fff' }} /> : isEditMode ? '✓ Save Roles' : 'Manage Roles'}
              </button>
            )}
          </div>
          <div style={cardBody}>
            {formData.Roles.length > 0 ? formData.Roles.map((role, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${C.cardBorder}` }}>
                <span style={{ fontSize: '12px', color: C.text }}>{role.RoleName}</span>
                <input type="checkbox" checked={role.IsSelected} onChange={() => handleChangeRole(index)} disabled={!isEditMode}
                  style={{ width: 14, height: 14, cursor: isEditMode ? 'pointer' : 'default', accentColor: C.accent }} />
              </div>
            )) : <p style={{ fontSize: '12px', color: C.muted }}>No roles available.</p>}
          </div>
        </div>

      </div>
    </div>
  );
};

export default UserDetail;
